/** Значок сервера и переключатель сообществ в шапке. */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ACCESS_LEVEL_LABEL } from '@ak/shared';

import { apiGet, apiLogout } from '../lib/api.ts';
import { selectGuild, type GuildAccess, type SelectedGuild } from '../lib/guilds.ts';
import { IconChevronDown, IconCheck, IconExternal, IconLogout } from './icons.tsx';
import { Badge, Button, Spinner } from './ui.tsx';

/** Значок сообщества: картинка сервера либо первая буква названия. */
export function GuildIcon({ name, iconUrl, size = 32 }: { name: string; iconUrl: string | null; size?: number }): ReactNode {
  if (iconUrl !== null) {
    return <img src={iconUrl} alt="" className="shrink-0 rounded-lg object-cover" style={{ width: size, height: size }} />;
  }

  return (
    <div
      className="grid shrink-0 place-items-center rounded-lg font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        background: 'var(--accent-soft)',
        color: 'var(--accent)',
      }}
      aria-hidden="true"
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

/**
 * Переключатель сервера.
 *
 * Список доступных сообществ запрашивается только при открытии: для него
 * сервер обращается к Discord за ролями, и делать это на каждой загрузке
 * страницы ради надписи в шапке не стоит.
 */
export function GuildSwitcher({ guild, inviteUrl }: { guild: SelectedGuild; inviteUrl: string | null }): ReactNode {
  const [open, setOpen] = useState(false);
  const [guilds, setGuilds] = useState<GuildAccess[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent): void => {
      if (container.current !== null && !container.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = (): void => {
    const next = !open;
    setOpen(next);

    if (next && guilds === null) {
      apiGet<{ guilds: GuildAccess[] }>('/api/guilds')
        .then((response) => {
          setGuilds(response.guilds);
        })
        .catch((cause: unknown) => {
          setError(cause instanceof Error ? cause.message : 'Не удалось загрузить список серверов');
        });
    }
  };

  const switchTo = (guildId: string): void => {
    if (guildId === guild.id) {
      setOpen(false);
      return;
    }

    setPending(guildId);
    selectGuild(guildId).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Не удалось переключить сервер');
      setPending(null);
    });
  };

  return (
    <div className="relative min-w-0" ref={container}>
      <button
        className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors"
        style={{ background: open ? 'var(--surface-hover)' : 'transparent', color: 'var(--text-primary)' }}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <GuildIcon name={guild.name} iconUrl={guild.iconUrl} size={26} />
        <span className="max-w-40 truncate lg:max-w-64">{guild.name}</span>
        <span style={{ color: 'var(--text-muted)' }}>
          <IconChevronDown size={16} />
        </span>
      </button>

      {open && (
        <div
          className="panel animate-in absolute top-full left-0 z-50 mt-1 w-72 overflow-hidden p-1"
          role="menu"
        >
          {error !== null && (
            <p className="px-3 py-2 text-sm" style={{ color: 'var(--danger)' }}>
              {error}
            </p>
          )}

          {guilds === null && error === null && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>
              <Spinner size={14} />
              Загрузка серверов…
            </div>
          )}

          {guilds?.map((item) => (
            <button
              key={item.guildId}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors"
              style={{ background: item.guildId === guild.id ? 'var(--accent-soft)' : 'transparent' }}
              onClick={() => {
                switchTo(item.guildId);
              }}
              disabled={pending !== null}
              role="menuitem"
            >
              <GuildIcon name={item.name} iconUrl={item.iconUrl} size={30} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{item.name}</span>
                <span className="block text-xs" style={{ color: 'var(--text-muted)' }}>
                  {ACCESS_LEVEL_LABEL[item.level]}
                  {item.configured ? '' : ' · не размечен'}
                </span>
              </span>
              {pending === item.guildId ? (
                <Spinner size={14} />
              ) : item.guildId === guild.id ? (
                <span style={{ color: 'var(--accent)' }}>
                  <IconCheck size={16} />
                </span>
              ) : null}
            </button>
          ))}

          {inviteUrl !== null && (
          <div className="mt-1 border-t pt-1" style={{ borderColor: 'var(--border-subtle)' }}>
            <a
              href={inviteUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-[var(--surface-hover)]"
              style={{ color: 'var(--accent)' }}
              role="menuitem"
            >
              <IconExternal size={15} />
              Добавить бота на другой сервер
            </a>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Строка списка серверов на отдельной странице выбора. */
export function GuildCard({
  guild,
  busy,
  onSelect,
}: {
  guild: GuildAccess;
  busy: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <button
      className="panel-interactive flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
      onClick={onSelect}
      disabled={busy}
    >
      <GuildIcon name={guild.name} iconUrl={guild.iconUrl} size={42} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{guild.name}</span>
        <span className="block text-xs" style={{ color: 'var(--text-muted)' }}>
          {ACCESS_LEVEL_LABEL[guild.level]}
        </span>
      </span>
      {guild.configured ? null : <Badge tone="warning">Не размечен</Badge>}
      {busy ? <Spinner size={16} /> : <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>→</span>}
    </button>
  );
}

/** Кнопка выхода — нужна и на экранах без бокового меню. */
export function LogoutButton(): ReactNode {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        void apiLogout();
      }}
    >
      <IconLogout size={16} />
      Выйти
    </Button>
  );
}
